import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {ApiModule} from './api.module';
import {BaseApiService} from './base-api.service';
import {HEADER_NO_LOADER} from '../constants';

@Injectable({
  providedIn: ApiModule,
  deps: [BaseApiService]
})
export class GeographicAddressMgtApiService {
  constructor(private http: BaseApiService) {
  }

  /**
   * It will return candidate matches based on this string and order results based on their perceived relevance
   * see https://vodafone-it.atlassian.net/wiki/spaces/DXL/pages/2004811888/V1+-+API+-+GeographicAddressManagement
   * @param streetName Name of the street or other street type.
   * @param provideAlternatives It triggers an autocomplete address request
   * @returns an observable of GeographicAddressAutocomplete
   */
  geographicAddressAutocomplete(
    streetName: string,
    provideAlternatives = true
  ): Observable<GeographicAddressAutocomplete> {
    this.http.header(HEADER_NO_LOADER, 'true');

    return this.http.post({
      path: '/geographicaddressmanagement/v1/geographicaddressvalidation',
      body: {
        streetName,
        provideAlternatives
      },
      hideError: true
    });
  }

  /**
   * To get as output the normalized address
   * see https://vodafone-it.atlassian.net/wiki/spaces/DXL/pages/2004811888/V1+-+API+-+GeographicAddressManagement
   * @param addressCode address unique identifier
   * @returns an observable of GeographicAddressValidation
   */
  geographicAddressValidation(addressCode: string): Observable<GeographicAddressValidation> {
    return this.http.post({
      path: '/geographicaddressmanagement/v1/geographicaddressvalidation',
      body: {
        addressCode
      },
      hideError: true
    });
  }

  /**
   * To get as output the normalized address
   * see https://vodafone-it.atlassian.net/wiki/spaces/DXL/pages/2004811888/V1+-+API+-+GeographicAddressManagement
   * @param normalizationAddress object of NormalizationAddress
   * @returns an observable of GeographicAddressValidation
   */
  geographicAddress(normalizationAddress: NormalizationAddress): Observable<GeographicAddressNormalization> {
    return this.http.post({
      path: '/geographicaddressmanagement/v1/geographicaddressvalidation',
      body: {
        ...normalizationAddress
      }
    });
  }
}


interface Street {
  id: string;
  streetName: string;
}

interface GeographicAddressAutocomplete {
  alternateGeographicAddress: Array<Street>;
}

interface StatusResponse{
  id: string;
  streetName: string;
}

export interface NormalizationAddress {
  streetName: string;
  streetNr: string;
  city: string;
  stateOrProvince: string;
  postcode: string;
  maxCandidatesNum?: number;
}

export interface ValidAddress {
  streetNr: string;
  streetName: string;
  postcode: string;
  city: string;
  stateOrProvince: string;
  country: string;
  geographicLocationRefOrValue?: any;
  addressCode?: string;
  streetNameNoDug?: string;
  istatCode?: string;
  candidatesNumber?: number;
  locality?: string;
}

export interface GeographicAddressValidation {
  validationResult: string;
  validAddress: ValidAddress;
}

export interface GeographicAddressNormalization {
  validAddress: ValidAddress;
  statusResponse: StatusResponse;
}
